import { useState, useEffect, useCallback } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FaBars, FaTimes, FaPhone, FaWhatsapp } from "react-icons/fa";
import { navLinks, contactInfo } from "../../data/content";
import Button from "../ui/Button";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const userInfo = localStorage.getItem('userInfo');
    setIsLoggedIn(!!userInfo);
  }, [location]);

  const handleLogout = () => {
    localStorage.removeItem('userInfo');
    setIsLoggedIn(false);
    navigate('/login');
  };

  /* ── Close on route change ── */
  useEffect(() => { setOpen(false); }, [location]);

  /* ── Body scroll lock ── */
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  /* ── Escape key close ── */
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") setOpen(false);
  }, []);
  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  /* ── Desktop link classname factory ── */
  const desktopLinkCls = ({ isActive }) => {
    const base = "relative px-3 py-2 text-sm lg:text-base font-medium tracking-wide transition-all duration-200 rounded-lg";
    if (isActive) {
      return `${base} text-primary font-semibold after:absolute after:bottom-0 after:left-3 after:right-3 after:h-0.5 after:bg-secondary after:rounded-full`;
    }
    return `${base} text-gray-700 hover:text-primary hover:bg-primary/5`;
  };

  /* ── Mobile link classname factory ── */
  const mobileLinkCls = ({ isActive }) =>
    `flex items-center px-4 py-3 border-b border-gray-100 text-lg font-medium transition-all duration-200 ${
      isActive ? "text-primary bg-gray-50" : "text-gray-800 hover:bg-gray-100 hover:text-primary"
    }`;

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-100 transition-all duration-300">

        {/* ── Top bar (desktop only) ─────────────────────────── */}
        <div className="hidden lg:block text-sm py-2 bg-primary-dark transition-colors duration-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 flex justify-between items-center">
            <span className="text-white/75">
              Your Trusted Partner in Finance, Legal, and Tech Solutions
            </span>
            <a
              href={`tel:${contactInfo.phone}`}
              className="flex items-center gap-2 hover:text-yellow-400 transition-colors"
              style={{ color: "var(--color-surface)" }}
            >
              <FaPhone className="text-yellow-500 text-xs" /> {contactInfo.phone}
            </a>
          </div>
        </div>

        {/* ── Main nav row ──────────────────────────────────── */}
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
          <div className="flex items-center justify-between h-16 lg:h-20">

            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 shrink-0 group">
              <img
                src="/logo.png"
                alt="Finegal Tech Solution"
                className="h-12 lg:h-14 w-auto object-contain transition-transform duration-200 group-hover:scale-105"
                onError={(e) => { e.currentTarget.style.display = "none"; setLogoError(true); }}
              />
              {logoError && (
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-base shrink-0"
                  style={{ background: "var(--gradient-gold)", color: "var(--color-primary-dark)" }}
                >
                  F
                </div>
              )}
            </Link>

            {/* Desktop nav links */}
            <div className="hidden lg:flex items-center gap-1 xl:gap-2">
              {navLinks.map((link) => (
                <NavLink key={link.path} to={link.path} className={desktopLinkCls}>
                  {link.label}
                </NavLink>
              ))}
            </div>

            {/* Desktop CTA */}
            <div className="hidden lg:block">
              {isLoggedIn ? (
                <Button onClick={handleLogout} variant="secondary" size="md">
                  Logout
                </Button>
              ) : (
                <Button href="/contact" variant="primary" size="md">
                  Get Free Consultation
                </Button>
              )}
            </div>

            {/* Mobile hamburger */}
            <button
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden p-2.5 rounded-xl text-primary-dark transition-all duration-200 hover:bg-gray-100"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={open ? "close" : "open"}
                  initial={{ opacity: 0, rotate: -90 }}
                  animate={{ opacity: 1, rotate: 0 }}
                  exit={{ opacity: 0, rotate: 90 }}
                  transition={{ duration: 0.15 }}
                  className="block"
                >
                  {open ? <FaTimes className="text-xl" /> : <FaBars className="text-xl" />}
                </motion.span>
              </AnimatePresence>
            </button>
          </div>
        </nav>
      </header>

      {/* ── Mobile drawer — rendered outside header so it truly covers all content ── */}
      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop */}
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm"
              style={{ zIndex: 9998 }}
              aria-hidden="true"
            />

            {/* Drawer panel */}
            <motion.div
              key="drawer"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.3, ease: "easeInOut" }}
              className="fixed top-0 right-0 bottom-0 w-[85%] max-w-[360px] bg-white flex flex-col shadow-2xl overflow-hidden"
              style={{ zIndex: 9999 }}
              role="dialog"
              aria-modal="true"
              aria-label="Mobile navigation menu"
            >
              {/* Drawer header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-white shrink-0">
                <Link to="/" onClick={() => setOpen(false)} className="flex items-center gap-3 min-w-0">
                  {!logoError ? (
                    <img
                      src="/logo.png"
                      alt="Finegal Tech Solution"
                      className="h-10 w-auto object-contain"
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-base shrink-0"
                        style={{ background: "var(--gradient-gold)", color: "var(--color-primary-dark)" }}
                      >
                        F
                      </div>
                  )}
                </Link>
                <button
                  onClick={() => setOpen(false)}
                  className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors shrink-0 ml-2"
                  aria-label="Close menu"
                >
                  <FaTimes className="text-lg" />
                </button>
              </div>

              {/* Nav links */}
              <nav className="flex-1 flex flex-col overflow-y-auto" role="navigation">
                {navLinks.map((link) => (
                  <NavLink
                    key={link.path}
                    to={link.path}
                    onClick={() => setOpen(false)}
                    className={mobileLinkCls}
                  >
                    {link.label}
                  </NavLink>
                ))}
              </nav>

              {/* CTA footer */}
              <div className="p-4 border-t border-gray-100 bg-gray-50 space-y-3 shrink-0">
                {isLoggedIn ? (
                  <Button
                    onClick={handleLogout}
                    variant="secondary"
                    size="md"
                    className="w-full !justify-center"
                  >
                    Logout
                  </Button>
                ) : (
                  <>
                    <Button
                      href="/contact"
                      variant="primary"
                      size="md"
                      className="w-full !justify-center"
                      onClick={() => setOpen(false)}
                    >
                      Get Free Consultation
                    </Button>
                    <a
                      href={`https://wa.me/${contactInfo.whatsapp}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 text-sm font-medium text-white bg-green-500 hover:bg-green-600 transition-colors py-2.5 rounded-lg"
                    >
                      <FaWhatsapp className="text-base" /> WhatsApp Us
                    </a>
                  </>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

